import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonIcon from '@mui/icons-material/Person';

const AboutSection: React.FC = () => {
  const theme = useTheme();

  const companyValues = [
    {
      icon: <VerifiedIcon />,
      title: 'Excellence',
      description: 'Nous visons l\'excellence dans chaque projet',
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Innovation',
      description: 'Technologies de pointe et solutions créatives',
    },
    {
      icon: <PersonIcon />,
      title: 'Proximité',
      description: 'Un accompagnement personnalisé pour chaque client',
    },
  ];

  return (
    <Box
      id="about"
      sx={{
        py: 10,
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            À Propos
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}
          >
            Une entreprise innovante au service de votre transformation digitale
          </Typography>
        </motion.div>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
          <Box>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
                EZTECH SOLUTIONS
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                Fondée en janvier 2024, EZTECH SOLUTIONS est une société de conseil et de services informatiques 
                spécialisée dans la transformation digitale des entreprises. Notre mission est d'accompagner nos 
                clients dans leur évolution technologique en proposant des solutions innovantes et sur mesure.
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                Basée à Rueil-Malmaison, notre équipe d'experts passionnés met son expertise au service de vos 
                projets les plus ambitieux. De la conception à la mise en œuvre, nous garantissons des solutions 
                performantes, sécurisées et évolutives.
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                Notre approche combine expertise technique, méthodologies agiles et écoute active pour comprendre 
                vos besoins spécifiques et vous proposer les solutions les plus adaptées.
              </Typography>
            </motion.div>
          </Box>

          <Box>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  backgroundColor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  Pourquoi nous choisir
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Expertise technique pointue"
                      secondary="Une équipe certifiée maîtrisant les dernières technologies"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Approche sur mesure"
                      secondary="Des solutions adaptées à vos besoins spécifiques"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Accompagnement complet"
                      secondary="Du conseil à la mise en production et au-delà"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Réactivité garantie"
                      secondary="Interlocuteur dédié et suivi de projet rigoureux"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Tarification transparente"
                      secondary="Devis détaillé et sans surprise"
                    />
                  </ListItem>
                </List>
              </Paper>
            </motion.div>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4, mt: 4 }}>
          {companyValues.map((value, index) => (
            <Box key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: 'background.default',
                    height: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: '50%',
                      backgroundColor: `${theme.palette.primary.main}15`,
                      color: 'primary.main',
                      mb: 2,
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default AboutSection;